import moment from "moment";
import { useEffect } from "react";

import Table from "@mui/material/Table";
import TableRow from "@mui/material/TableRow";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import BusinessIcon from "@mui/icons-material/Business";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import { visuallyHidden } from "@mui/utils";
import {
  Box,
  Chip,
  Container,
  Paper,
  TableContainer,
  TablePagination,
  TableSortLabel,
  Tooltip,
} from "@mui/material";

import conf from "../../conf";
import Create from "./Create";
import Refresh from "../utils/Refresh";
import ManageOrg from "./ManageOrg";
import { useAuth } from "../auth/AuthProvider";
import LinearLoader from "../utils/LinearLoader";
import SectionHeader from "../utils/SectionHeader";
import usePagination from "../../hooks/pagination";
import ProtectedRoute from "../auth/ProtectedRoute";
import DescriptionWithEdit from "../utils/Description";
import { Organization, useConfgSysOrganizations } from "../../hooks/confg/base";

type OrgRowProps = { org: Organization; isAdmin: boolean };

const OrgRow = (props: OrgRowProps) => {
  const { id, name, activeUsers, description, domain, online, lastActive } =
    props.org;

  const Status = (
    <Chip
      size="small"
      label={online ? "online" : "offline"}
      color={online ? "success" : "error"}
    />
  );

  return (
    <TableRow sx={conf.STYLE.LAST_CHILD_NO_BORDER}>
      <TableCell component="th" scope="row">
        <Chip size="small" label={name} />
      </TableCell>

      <TableCell align="right">{Status}</TableCell>

      <TableCell align="right">{domain}</TableCell>

      <TableCell align="right">{activeUsers}</TableCell>

      <TableCell align="right">
        <DescriptionWithEdit
          id={id}
          type="organization"
          readOnly={false}
          description={description || "--"}
        />
      </TableCell>

      <TableCell align="right">{moment(lastActive).fromNow()}</TableCell>

      <TableCell align="right">
        <ManageOrg organization={props.org} />
      </TableCell>
    </TableRow>
  );
};

const Organizations = () => {
  const { user: self } = useAuth();
  const isAdmin = self.activeOrganization.role === "sys.admin";

  const paginate = usePagination({ defaultSort: "name", defaultOrder: "asc" });
  const { order, sort, page, limit, body } = paginate;
  const { handleChangeLimit, handleChangePage, handleSort } = paginate;

  const orgs = useConfgSysOrganizations(body);

  useEffect(() => {
    document.title = "[semiauth] | organizations";
  }, []); // run once on mount

  return (
    <ProtectedRoute>
      <Container maxWidth="xl" sx={{ marginBottom: 4 }}>
        <SectionHeader
          title="Organizations"
          icon={<BusinessIcon />}
          buttons={[
            ...(isAdmin ? [<Create key={"orgs-create"} />] : []),
            <TablePagination
              key={"orgs-pagination"}
              component="div"
              rowsPerPageOptions={[10, 25, 50, 100]}
              count={orgs?.data?.meta.total || 0}
              page={page}
              rowsPerPage={limit}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeLimit}
            />,
            <Refresh
              key={"orgs-refresh"}
              onClick={orgs.refetch}
              isFetching={orgs.isFetching}
            />,
          ]}
        />

        <Paper elevation={3}>
          <TableContainer sx={{ maxHeight: 630 }}>
            <Table aria-label="collapsible table" stickyHeader>
              <TableHead>
                <TableRow>
                  {conf.COLUMNS.SYS.ORGS.map((column) => (
                    <TableCell
                      key={column.key}
                      sortDirection={order}
                      align={column.isHeader ? "left" : "right"}
                    >
                      <TableSortLabel
                        active={sort === column.key}
                        direction={sort === column.key ? order : "asc"}
                        onClick={(event: React.MouseEvent<unknown>) =>
                          handleSort(event, column.key)
                        }
                      >
                        {column.description ? (
                          <Tooltip title={column.description} placement="top">
                            <b>{column.label}</b>
                          </Tooltip>
                        ) : (
                          <b>{column.label}</b>
                        )}

                        {sort === column.key ? (
                          <Box component="span" sx={visuallyHidden}>
                            {order === "desc"
                              ? "sorted descending"
                              : "sorted ascending"}
                          </Box>
                        ) : null}
                      </TableSortLabel>
                    </TableCell>
                  ))}

                  {isAdmin && (
                    <TableCell align="right">
                      <AssignmentIndIcon fontSize="inherit" />
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>

              {orgs.isLoading ? (
                <TableBody>
                  <TableRow>
                    <TableCell colSpan={8}>
                      <LinearLoader />
                    </TableCell>
                  </TableRow>
                </TableBody>
              ) : (
                <TableBody>
                  {orgs?.data?.data?.map((org) => (
                    <OrgRow key={org.id} org={org} isAdmin={isAdmin} />
                  ))}
                </TableBody>
              )}
            </Table>
          </TableContainer>
        </Paper>
      </Container>
    </ProtectedRoute>
  );
};

export default Organizations;
